import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';
import { Link } from 'react-router-dom';

const GumDisease = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Gum Disease Treatment Fort Worth TX | Periodontal Care | Fort Worth Dental</title>
        <meta name="description" content="Expert gum disease treatment in Fort Worth TX. Periodontal therapy, deep cleaning, LANAP laser treatment. Stop gum disease progression. Call (817) 985-3337." />
        <meta name="keywords" content="gum disease fort worth, periodontal treatment fort worth tx, gum infection, gingivitis treatment, periodontitis, bleeding gums" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/dentist-examining.jpg)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Gum Disease Treatment in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Comprehensive Periodontal Care</p>
            <p className="text-sm mt-4 opacity-90">Periodontal Disease Treatment | Gingivitis Care | LANAP Laser Therapy</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* What Is Gum Disease */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/gum-disease-new-1.jpg" 
                  alt="Gum disease periodontal treatment Fort Worth TX - Signs and symptoms of gingivitis and periodontitis" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examining.jpg'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is Gum Disease?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  Gum disease, also known as periodontal disease, is an infection of the gums and bone that hold your teeth in place. It's caused by bacteria in plaque that accumulates on teeth and hardens into tartar. If left untreated, gum disease can lead to tooth loss and has been linked to other health problems throughout the body.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Gum disease typically progresses through stages, starting with gingivitis (inflammation of the gums) and advancing to periodontitis (inflammation and infection of the deeper supporting structures). Early detection and treatment are crucial to prevent permanent damage.
                </p>
              </div>
            </div>
          </section>

          {/* Warning Signs */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Warning Signs of Gum Disease</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Many people don't realize they have gum disease because it's often painless in the early stages. Watch for these warning signs:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Gums that bleed easily when brushing or flossing</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Red, swollen, or tender gums</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Gums that have pulled away from teeth (recession)</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Persistent bad breath or bad taste</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Loose or separating teeth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Pus between teeth and gums</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Changes in the way your teeth fit together when you bite</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">•</span>
                <span>Changes in the fit of partial dentures</span>
              </li>
            </ul>
          </section>

          {/* Treatment Options */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Gum Disease Treatment Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <img 
                  src="/images/services/gum-disease-new-2.jpg" 
                  alt="Professional gum disease treatment at Fort Worth Dental - Scaling, root planing, and LANAP laser therapy" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
              <div>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  At Fort Worth Dental, we offer a range of treatment options depending on the severity of your gum disease:
                </p>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Non-Surgical Treatments:</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-teal-600 mr-2">✓</span>
                        <span>Professional dental cleaning</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-teal-600 mr-2">✓</span>
                        <span>Scaling and root planing (deep cleaning)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-teal-600 mr-2">✓</span>
                        <span>Antibiotics or antimicrobial mouth rinses</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Surgical Treatments:</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-teal-600 mr-2">✓</span>
                        <span><Link to="/lanap" className="text-teal-600 hover:underline">LANAP® laser treatment</Link></span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-teal-600 mr-2">✓</span>
                        <span>Flap surgery (pocket reduction)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-teal-600 mr-2">✓</span>
                        <span>Bone and tissue grafts</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Prevention */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Preventing Gum Disease</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The best treatment for gum disease is prevention. Follow these steps to maintain healthy gums:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Brush teeth at least twice daily with fluoride toothpaste</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Floss daily to remove plaque between teeth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Visit your dentist regularly for professional cleanings and exams</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Don't smoke or use tobacco products</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Eat a balanced diet</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Manage conditions like diabetes that can increase risk</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Gum Disease Treatment?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced periodontal team offers the most advanced treatments including LANAP® laser therapy.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">LANAP® Certified</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Don't Wait - Treat Gum Disease Today</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental for comprehensive gum disease treatment in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Gum Disease Treatment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default GumDisease;